import React, { memo } from 'react';
import { Category } from '../../types';
import { useCurrency } from '../../contexts/CurrencyContext';

// Product tipini burada tanımlayalım
interface Product {
  id: number;
  name: string;
  price: number;
  cost_price?: number; // Geliş fiyatı (opsiyonel)
  tax_rate: number;
  category_id: number;
  image_url?: string | null;
  stock_quantity?: number | null;
  is_active: boolean;
  created_at: string;
  category?: Category;
}

interface ProductCardProps {
  product: Product;
  onAddToCart?: (product: Product) => void;
  className?: string;
}

const ProductCard: React.FC<ProductCardProps> = ({ 
  product, 
  onAddToCart,
  className = '' 
}) => {
  const { formatPrice } = useCurrency();
  return (
    <div className={`bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden ${className}`}>
      {/* Ürün Görseli */}
      <div className="relative h-32 bg-gray-100">
        {product.image_url ? (
          <img
            src={product.image_url && product.image_url.startsWith('http') ? product.image_url : `${process.env.REACT_APP_API_URL || window.location.origin}${product.image_url || ''}`}
            alt={product.name}
            className="w-full h-full object-cover"
            onLoad={() => {
              if (product.image_url) {
                // Image loaded successfully
              }
            }}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              if (product.image_url) {
                // ProductCard görsel yüklenemedi
              }
              target.style.display = 'none';
              target.parentElement!.innerHTML = '<div class="flex items-center justify-center h-full text-gray-400"><span class="text-2xl">🖼️</span></div>';
            }}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400">
            <span className="text-3xl">🍽️</span>
          </div>
        )}
      </div>
      
      <div className="p-4">
        <h3 className="font-medium text-gray-900 dark:text-dark-text mb-2">
          {product.name}
        </h3>

        {/* Stok Bilgisi */}
        {product.stock_quantity !== null && product.stock_quantity !== undefined && (
          <div className="mb-2">
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              product.stock_quantity > 0
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {product.stock_quantity > 0 ? `${product.stock_quantity} adet` : 'Stok yok'}
            </span>
          </div>
        )}

        <div className="flex items-center justify-between">
          <span className="text-lg font-bold text-cafe-brown dark:text-cafe-light">
            {formatPrice(product.price)}
          </span>
          {onAddToCart && (
            <button
              onClick={() => onAddToCart(product)}
              className="bg-cafe-brown text-white px-3 py-1 rounded-md text-sm hover:bg-cafe-dark transition-colors"
            >
              Ekle
            </button>
          )}
        </div>

        {/* Kar Bilgisi */}
        {(() => {
          // Debug: cost_price var mı kontrol et
          if (!product.cost_price || product.cost_price <= 0) {
            return null;
          }

          const costPrice = typeof product.cost_price === 'string' ? parseFloat(product.cost_price) : product.cost_price;
          const salePrice = typeof product.price === 'string' ? parseFloat(product.price) : product.price;
          const taxRate = typeof product.tax_rate === 'string' ? parseFloat(product.tax_rate) : product.tax_rate;
          
          // Net satış fiyatı (vergisiz)
          const netSalePrice = salePrice / (1 + taxRate / 100);
          
          // Kar (Euro)
          const profit = netSalePrice - costPrice;
          
          // Kar (%)
          const profitPercent = (profit / costPrice) * 100;
          
          return (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-dark-border">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">💰 Kar:</span>
                <span className={`font-medium ${profit > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {formatPrice(profit)} / {profitPercent.toFixed(0)}%
                </span>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default memo(ProductCard);
